'use client';

import { env } from '@prismo-io/env';
import {
  type Database,
  LanguageCode,
  type LanguageCodeT,
} from '@prismo-io/schemas';
import { createBrowserClient } from '@supabase/ssr';
import type { AuthFlowType, SupabaseClient } from '@supabase/supabase-js';
import { cookies } from './utils/cookies';

type Options = {
  locale: LanguageCodeT;
  flowType: AuthFlowType;
};

const DEFAULT_OPTIONS: Options = {
  locale: LanguageCode.Enum.fr,
  flowType: 'pkce',
};

export const createApiClient = (
  options: Partial<Options> = DEFAULT_OPTIONS
): SupabaseClient<Database> => {
  const { locale } = {
    ...DEFAULT_OPTIONS,
    ...options,
  };

  const cookieStore = cookies();

  const poCookies: Record<string, string> = Object.entries(cookieStore)
    .map(([k, v]) => {
      return {
        name: k,
        value: v,
      };
    })
    .filter((v) => v.name.startsWith('po-'))
    .reduce((ac, crt) => ({ ...ac, [crt.name]: crt.value }), {});

  const client = createBrowserClient<Database>(
    `${env.NEXT_PUBLIC_BASE_API_URL}/api`,
    env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
    {
      auth: {
        flowType: options.flowType ?? 'pkce',
        storageKey: env.NEXT_PUBLIC_COOKIE_NAME,
      },
      global: {
        headers: {
          'x-language': locale,
          ...poCookies,
        },
        fetch: (input, init = {}) => {
          return fetch(input, { ...init, credentials: 'same-origin' });
        },
      },
      cookieOptions: {
        domain: env.NEXT_PUBLIC_COOKIE_DOMAIN,
        name: env.NEXT_PUBLIC_COOKIE_NAME,
        sameSite: 'lax',
        path: '/',
        secure: !env.NEXT_PUBLIC_COOKIE_DOMAIN.startsWith('localhost'),
      },
    }
  );

  client.realtime.endPoint =
    `${env.NEXT_PUBLIC_SUPABASE_URL}/realtime/v1/websocket`.replace(
      /^http/i,
      'ws'
    );

  return client;
};
